// api/lease.ts - LeaseController endpoints (tenant контекст)

import { AxiosResponse } from 'axios';
import $api from './instance';
import {
  TenantLeaseListItem,
  TenantLeaseDetails,
  TerminateLeaseResponse
} from '@/types/entities';
import {
  TenantLeasesFilter,
  TerminateLeaseRequest
} from '@/types/api-types';

export interface Address {
    id: number;
    country: string;
    stateOrProvince: string;
    city: string;
    street: string;
    buildingNumber: string;
    municipality: string | null;
    postalCode: string;
    latitude: number;
    longitude: number;
    phone: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface Property {
    id: number;
    ownerId: number;
    name: string;
    type: string;
    constructionYear: number | null;
    foundation: string | null;
    floors: number | null;
    dimension: number | null;
    hasBasement: boolean;
    basementDimension: number | null;
    basementDescription: string | null;
    hasExterior: boolean;
    exteriorDimension: number | null;
    exteriorZoning: string | null;
    hasRestrictions: boolean;
    restrictionsDescription: string | null;
    createdAt: string;
    updatedAt: string;
    address?: Address; // Address is now part of Property
}

export interface Unit {
    id: number;
    number: string;
    createdAt: string;
    updatedAt: string;
    type: number;
    floor: string;
    rooms: number;
    dimension: number | null;
    bedrooms: number | null;
    numberOfFloors: number | null;
    bathrooms: number | null;
    roomsList: any[];
    characteristics: any | null;
    property: Property;
}

export interface MyLease {
    id: number;
    unitId: number;
    tenantId: number;
    startDate: string | null;
    endDate: string | null;
    status: string;
    monthlyRent: string;
    depositAmount: string | null;
    paymentDay: number | null;
    leaseType: string | null;
    costOfServices: string | null;
    costOfServicesPeriod: string | null;
    totalRentPrice: string | null;
    totalRentPricePeriod: string | null;
    rentPriceDescription: string | null;
    firstPaymentDate: string | null;
    paymentMethod: string | null;
    paymentMethodOtherSpecify: string | null;
    inviteEmail: string | null;
    createdAt: string;
    updatedAt: string;
    unit: Unit;
}

// === LEASE API для TENANT ===

/**
 * Отримати всі lease tenant'a (активні + історія)
 * GET /lease/my
 */
export async function getTenantLeasesRequest(
  filters?: TenantLeasesFilter,
): Promise<AxiosResponse<TenantLeaseListItem[]>> {
  const searchParams = new URLSearchParams();

  if (filters?.status) {
    searchParams.set('status', filters.status);
  }
  if (filters?.propertyId) {
    searchParams.set('propertyId', filters.propertyId.toString());
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/lease/my?${queryString}` : '/lease/my';

  return $api.get<TenantLeaseListItem[]>(url);
}

/**
 * Отримати всі лізинги для поточного тенанта
 * GET /api/lease/my
 */
export async function getMyLeasesRequest(): Promise<AxiosResponse<MyLease[]>> {
  return $api.get<MyLease[]>('/lease/my');
}

/**
 * Отримати деталі конкретного lease
 * GET /lease/:id
 */
export async function getTenantLeaseDetailsRequest(
  leaseId: number,
): Promise<AxiosResponse<TenantLeaseDetails>> {
  return $api.get<TenantLeaseDetails>(`/lease/${leaseId}`);
}

/**
 * Достроково завершити lease (якщо дозволено)
 * PATCH /lease/:id/terminate
 */
export async function terminateTenantLeaseRequest(
  leaseId: number,
  data?: TerminateLeaseRequest,
): Promise<AxiosResponse<TerminateLeaseResponse>> {
  return $api.patch<TerminateLeaseResponse>(
    `/lease/${leaseId}/terminate`,
    data,
  );
}

// === ДОПОМІЖНІ ФУНКЦІЇ ===

/**
 * Отримати тільки активні lease
 */
export async function getTenantActiveLeasesRequest(): Promise<
  AxiosResponse<TenantLeaseListItem[]>
> {
  return getTenantLeasesRequest({ status: 'active' });
}

/**
 * Отримати історію lease (завершені)
 */
export async function getTenantLeaseHistoryRequest(): Promise<
  AxiosResponse<TenantLeaseListItem[]>
> {
  return getTenantLeasesRequest({ status: 'terminated' });
}

/**
 * Отримати lease для конкретної property
 */
export async function getTenantLeasesForPropertyRequest(
  propertyId: number,
): Promise<AxiosResponse<TenantLeaseListItem[]>> {
  return getTenantLeasesRequest({ propertyId });
}

// === LEGACY FUNCTIONS ===

/**
 * @deprecated Використовуйте нові хуки для tenant lease
 */
export async function getRentedPlacesRequest(): Promise<any[]> {
  const res = await $api.get('/rented-places');
  return res.data;
}

export async function getRentedPlaceRequest(id: number): Promise<any> {
  const res = await $api.get('/rented-places/' + id);
  return res.data;
}
